import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm animate-fade-up" data-testid="login-card">
        <div className="flex justify-center mb-10">
          <Brand size="lg" />
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-extrabold tracking-tight text-brand-graphite">
            Bienvenido de nuevo
          </h1>
          <p className="text-brand-gray/70 mt-2 text-sm">
            Inicia sesión para gestionar tu negocio de alquiler.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-semibold text-brand-graphite">
              Correo electrónico
            </Label>
            <Input
              id="email"
              type="text"
              autoComplete="username"
              data-testid="login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@empresa.com"
              className="h-11 border-gray-300 focus-visible:ring-brand-graphite"
              required
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-sm font-semibold text-brand-graphite">
                Contraseña
              </Label>
              <button
                type="button"
                data-testid="forgot-password-link"
                className="text-xs font-semibold text-brand-gray/70 hover:text-brand-graphite transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              data-testid="login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-11 border-gray-300 focus-visible:ring-brand-graphite"
              required
            />
          </div>

          {error && (
            <p className="text-sm font-medium text-red-600" data-testid="login-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full h-11 bg-brand-yellow hover:bg-[#e6ac00] text-brand-graphite font-bold text-sm rounded-md group transition-colors"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Iniciar sesión
                <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </Button>
        </form>

        <p className="text-center text-xs text-brand-gray/50 mt-10">
          MyFriendRent · Plataforma de gestión para empresas de alquiler
        </p>
      </div>
    </div>
  );
}
